import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export type CheckoutItemKind = 'subscription' | 'credits';

interface CheckoutConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Display name of the product (e.g. "Profesjonalny", "Maksymalny", "Pakiet kredytów") */
  productName: string;
  /** Price in PLN (without currency suffix) */
  price: number;
  /** Whether this is a recurring subscription or a one-off credits purchase */
  kind: CheckoutItemKind;
  /** Loading state — true while redirecting to PayU */
  isLoading?: boolean;
}

/**
 * Checkout confirmation modal — required by Polish ustawa o prawach konsumenta
 * (art. 17 ust. 3, "rozwiązanie przyciskowe") and PayU's merchant requirements.
 *
 * Shows the user the order summary, fulfillment info, and the legally-mandated
 * "Zamawiam i płacę" button before redirecting to PayU.
 */
export default function CheckoutConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  productName,
  price,
  kind,
  isLoading = false,
}: CheckoutConfirmModalProps) {
  const { t } = useTranslation();

  // Close on Escape (unless loading — don't lose user's payment in flight)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const isSubscription = kind === 'subscription';
  const billingPeriod = isSubscription
    ? t('pricing.checkoutConfirm.monthly')
    : t('pricing.checkoutConfirm.creditsBillingPeriod');
  const fulfillmentText = isSubscription
    ? t('pricing.checkoutConfirm.fulfillmentInfo')
    : t('pricing.checkoutConfirm.creditsFulfillmentInfo');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => !isLoading && onClose()}
        aria-hidden="true"
      />

      {/* Card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-confirm-title"
        className="relative z-10 w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700">
          <h2
            id="checkout-confirm-title"
            className="text-xl font-bold text-slate-900 dark:text-white"
          >
            {t('pricing.checkoutConfirm.title')}
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Order summary */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-3">
              {t('pricing.checkoutConfirm.summaryHeading')}
            </h3>
            <dl className="space-y-2.5">
              <div className="flex items-baseline justify-between">
                <dt className="text-sm text-slate-600 dark:text-slate-400">
                  {t('pricing.checkoutConfirm.productLabel')}
                </dt>
                <dd className="text-sm font-semibold text-slate-900 dark:text-white text-right ml-4">
                  {productName}
                </dd>
              </div>
              <div className="flex items-baseline justify-between">
                <dt className="text-sm text-slate-600 dark:text-slate-400">
                  {t('pricing.checkoutConfirm.billingPeriod')}
                </dt>
                <dd className="text-sm font-semibold text-slate-900 dark:text-white text-right ml-4">
                  {billingPeriod}
                </dd>
              </div>
              <div className="flex items-baseline justify-between pt-2.5 border-t border-slate-200 dark:border-slate-700">
                <dt className="text-sm text-slate-600 dark:text-slate-400">
                  {t('pricing.checkoutConfirm.priceLabel')}
                </dt>
                <dd className="text-right ml-4">
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">
                    {price} PLN
                    {isSubscription && (
                      <span className="text-sm font-normal text-slate-500 dark:text-slate-400 ml-1">
                        {t('pricing.checkoutConfirm.perMonth')}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {t('pricing.checkoutConfirm.vatIncluded')}
                  </div>
                </dd>
              </div>
            </dl>
          </section>

          {/* Fulfillment info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs text-blue-900 dark:text-blue-200 leading-relaxed">
            {fulfillmentText}
          </div>

          {/* Withdrawal notice — required for digital services per ustawa o prawach konsumenta */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
            {t('pricing.checkoutConfirm.withdrawalNotice')}
          </div>

          {/* Terms acknowledgment */}
          <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
            <Trans
              i18nKey="pricing.checkoutConfirm.termsAcknowledgment"
              components={{
                termsLink: (
                  <Link
                    to="/regulamin"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  />
                ),
                privacyLink: (
                  <Link
                    to="/polityka-prywatnosci"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  />
                ),
              }}
            />
          </p>
        </div>

        {/* Footer with buttons */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-700 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t('pricing.checkoutConfirm.cancelButton')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {isLoading
              ? t('pricing.checkoutConfirm.redirectingToPayU')
              : t('pricing.checkoutConfirm.confirmButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
